import { type ProviderCapabilityStatus } from '../consts/provider-capabilities';
import { type CompanyStatisticsAttribution } from './company-statistics.types';

// ==================== ENUMS ====================

export enum ProviderType {
  forwarder = 'forwarder',
  nvocc = 'nvocc',
  trucking = 'trucking',
}

export enum TransportMode {
  ocean = 'ocean',
  road = 'road',
  rail = 'rail',
}

export enum Tier {
  free = 'free',
  pro = 'pro',
  enterprise = 'enterprise',
}

export enum ClaimStatus {
  unclaimed = 'unclaimed',
  pending = 'pending',
  claimed = 'claimed',
}

export enum AnnualVolumeTeu {
  up_to_1k = 'up_to_1k',
  from_1k_to_5k = 'from_1k_to_5k',
  from_5k_to_20k = 'from_5k_to_20k',
  from_20k_to_100k = 'from_20k_to_100k',
  from_100k_to_500k = 'from_100k_to_500k',
  over_500k = 'over_500k',
}

export enum EmployeeCountRange {
  one = 'one',
  from_2_to_10 = 'from_2_to_10',
  from_11_to_50 = 'from_11_to_50',
  from_51_to_200 = 'from_51_to_200',
  from_201_to_500 = 'from_201_to_500',
  from_501_to_1000 = 'from_501_to_1000',
  over_1000 = 'over_1000',
}

export const EMPLOYEE_COUNT_RANGE_NOT_SPECIFIED = 'not_specified' as const;
export type EmployeeCountRangeFilterValue =
  | EmployeeCountRange
  | typeof EMPLOYEE_COUNT_RANGE_NOT_SPECIFIED;
export const EMPLOYEE_COUNT_RANGE_FILTER_VALUES: EmployeeCountRangeFilterValue[] =
  [
    EmployeeCountRange.one,
    EmployeeCountRange.from_2_to_10,
    EmployeeCountRange.from_11_to_50,
    EmployeeCountRange.from_51_to_200,
    EmployeeCountRange.from_201_to_500,
    EmployeeCountRange.from_501_to_1000,
    EmployeeCountRange.over_1000,
    EMPLOYEE_COUNT_RANGE_NOT_SPECIFIED,
  ];

export const ProviderRatingModifierCategory = {
  fast_response: 'fast_response',
  slow_response: 'slow_response',
  no_response: 'no_response',
  win: 'win',
  editorial: 'editorial',
} as const;
export type ProviderRatingModifierCategory =
  (typeof ProviderRatingModifierCategory)[keyof typeof ProviderRatingModifierCategory];

// ==================== REQUEST INTERFACES (Client-safe) ====================

export interface CreateProviderData {
  companyName: string;
  slug?: string;
  providerTypes: ProviderType[];
  transportModes: TransportMode[];
  providerTypeCapabilities?: {
    code: ProviderType;
    status: ProviderCapabilityStatus;
  }[];
  transportModeCapabilities?: {
    code: TransportMode;
    status: ProviderCapabilityStatus;
  }[];
  tier?: Tier;
  claimStatus?: ClaimStatus;
  needsReview?: boolean;
  description?: string;
  websiteUrl: string;
  hideWebsiteUrl?: boolean;
  logoUrl?: string;
  trustScore?: number;
  isActive?: boolean;
  receivesRateRequests?: boolean;
  acceptsIndividualShippers?: boolean | null;
  languages?: string[];
  tagline?: string;
  foundedYear?: number;
  annualVolumeTeu?: AnnualVolumeTeu;
  employeeCountRange?: EmployeeCountRange;
}

export interface UpdateProviderData {
  companyName?: string;
  providerTypes?: ProviderType[];
  transportModes?: TransportMode[];
  tier?: Tier;
  claimStatus?: ClaimStatus;
  needsReview?: boolean;
  description?: string;
  websiteUrl?: string;
  hideWebsiteUrl?: boolean;
  logoUrl?: string;
  isLight?: boolean;
  trustScore?: number | null;
  isActive?: boolean;
  receivesRateRequests?: boolean;
  acceptsIndividualShippers?: boolean | null;
  languages?: string[];
  tagline?: string;
  foundedYear?: number | null;
  annualVolumeTeu?: AnnualVolumeTeu | null;
  employeeCountRange?: EmployeeCountRange | null;
}

export interface GetProvidersParams {
  page?: number;
  limit?: number;
  search?: string;
  tier?: Tier;
  claimStatus?: ClaimStatus;
  /** When true/false, filter providers flagged for post-import QA */
  needsReview?: boolean;
  providerTypes?: ProviderType[];
  transportModes?: TransportMode[];
  isActive?: boolean;
  receivesRateRequests?: boolean;
  acceptsIndividualShippers?: boolean | null;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  ids?: string[];
  excludeIds?: string[];
  hasEmail?: boolean;
  officeCountryIds?: string[];
  headquartersCountryIds?: string[];
  coverageCountryIds?: string[];
  serviceIds?: string[];
  certificationIds?: string[];
  employeeCountRanges?: EmployeeCountRangeFilterValue[];
}

export interface BulkActivateProvidersData {
  ids: string[];
  isActive: boolean;
}

// ==================== TRANSLATION INTERFACES ====================

export interface UpsertProviderTranslationData {
  languageCode: string;
  companyName?: string;
  description?: string;
  tagline?: string;
  source?: string;
  isPrimary?: boolean;
}

// ==================== RESPONSE DTOs ====================

export interface ProviderTranslationResponseDto {
  id: string;
  providerId: string;
  languageCode: string;
  companyName: string | null;
  description: string | null;
  tagline: string | null;
  source: string | null;
  isPrimary: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProviderResponseDto {
  id: string;
  companyName: string;
  slug: string;
  providerTypes: ProviderType[];
  transportModes: TransportMode[];
  tier: Tier;
  claimStatus: ClaimStatus;
  needsReview: boolean;
  description: string | null;
  websiteUrl: string | null;
  hideWebsiteUrl: boolean;
  domain: string | null;
  logoUrl: string | null;
  isLight?: boolean;
  linkedinUrl: string | null;
  facebookUrl: string | null;
  instagramUrl: string | null;
  xUrl: string | null;
  trustScore: number | null;
  isActive: boolean;
  receivesRateRequests: boolean;
  acceptsIndividualShippers: boolean | null;
  languages: string[];
  tagline: string | null;
  foundedYear: number | null;
  annualVolumeTeu: AnnualVolumeTeu | null;
  employeeCountRange: EmployeeCountRange | null;
  createdAt: Date;
  updatedAt: Date;
  translations?: ProviderTranslationResponseDto[];
}

export interface ProviderDetailResponseDto extends ProviderResponseDto {
  translations: ProviderTranslationResponseDto[];
  countryCoverages: import('./coverage.types').CountryCoverageResponseDto[];
  routes: import('./coverage.types').RouteResponseDto[];
  offices: import('./coverage.types').OfficeResponseDto[];
  services: import('./reference-data.types').ProviderServiceResponseDto[];
  associations: import('./reference-data.types').ProviderAssociationResponseDto[];
  certifications: import('./reference-data.types').ProviderCertificationResponseDto[];
  shipmentTypes: import('./reference-data.types').ProviderShipmentTypeResponseDto[];
  commodities: import('./reference-data.types').ProviderCommodityResponseDto[];
  providerTypeCapabilities: import('./reference-data.types').ProviderTypeCapabilityResponseDto[];
  transportModeCapabilities: import('./reference-data.types').ProviderTransportModeCapabilityResponseDto[];
  emails: import('./provider-contact.types').ProviderEmailResponseDto[];
  phones: import('./provider-contact.types').ProviderPhoneResponseDto[];
  domains: import('./provider-contact.types').ProviderDomainResponseDto[];
  featuredStatus?: import('./plan.types').ProviderFeaturedStatusDto;
}

export interface ProvidersResponseDto {
  data: ProviderResponseDto[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ==================== PUBLIC DTOs (no email exposed) ====================

export interface PublicProviderResponseDto {
  id: string;
  companyName: string;
  slug: string;
  providerTypes: ProviderType[];
  transportModes: TransportMode[];
  tier: Tier;
  claimStatus: ClaimStatus;
  description: string | null;
  websiteUrl: string | null;
  logoUrl: string | null;
  isLight?: boolean;
  languages: string[];
  tagline: string | null;
  foundedYear: number | null;
  annualVolumeTeu: AnnualVolumeTeu | null;
  employeeCountRange: EmployeeCountRange | null;
  updatedAt: Date;
}

export interface PublicProviderListItemDto {
  companyName: string;
  slug: string;
  providerTypes: ProviderType[];
  transportModes: TransportMode[];
  tier: Tier;
  claimStatus: ClaimStatus;
  logoUrl: string | null;
  isLight?: boolean;
  languages: string[];
  tagline: string | null;
}

export interface PublicProviderDetailResponseDto
  extends PublicProviderResponseDto {
  statistics?: CompanyStatisticsAttribution;
  contactStatistics?: Partial<Record<'profile' | 'provider_rates', Partial<Record<'website', CompanyStatisticsAttribution>>>>;
  countryCoverages: import('./coverage.types').CountryCoverageSummaryDto[];
  routes: import('./coverage.types').RouteSummaryDto[];
  offices: import('./coverage.types').OfficeSummaryDto[];
  services: import('./reference-data.types').ServiceSummaryDto[];
  associations: import('./reference-data.types').AssociationSummaryDto[];
  certifications: import('./reference-data.types').CertificationSummaryDto[];
  shipmentTypes: import('./reference-data.types').ShipmentTypeSummaryDto[];
  commodities: import('./reference-data.types').CommoditySummaryDto[];
  mapData: ProviderMapDataDto;
}

export interface ProviderMapDataDto {
  countryCoverages: {
    countryId: string;
    countryName: string;
    isoCountryCode: string | null;
    isFeatured: boolean;
    bbox: [number, number, number, number] | null;
    coordinates: [number, number] | null;
    regions?: {
      geoObjectId: string;
      name: string;
      isFeatured: boolean;
      isExcluded: boolean;
    }[];
  }[];
  routes: {
    origin: {
      name: string;
      coordinates: [number, number] | null;
      /** Geo object id of the route endpoint (port, country, etc.) */
      geoObjectId: string | null;
      isoCountryCode: string | null;
      /** Country geo object id used to match `countryCoverages[].countryId` (parent country for ports/regions) */
      coverageCountryId: string | null;
      /**
       * When the endpoint is a trade region, member country geo ids (from `geo_object_relationship`)
       * so the map can highlight the same polygons as standard country borders.
       */
      tradeRegionCountryIds?: string[];
    };
    destination: {
      name: string;
      coordinates: [number, number] | null;
      geoObjectId: string | null;
      isoCountryCode: string | null;
      coverageCountryId: string | null;
      tradeRegionCountryIds?: string[];
    };
    transportMode: TransportMode;
    isFeatured: boolean;
  }[];
}

export interface PaginatedPublicProvidersResponseDto {
  data: PublicProviderListItemDto[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PublicProviderSitemapEntryDto {
  id: string;
  slug: string;
  updatedAt: Date;
  hasCurrentPublicRates: boolean;
}

export interface PaginatedPublicProviderSitemapResponseDto {
  data: PublicProviderSitemapEntryDto[];
  rowCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** An admin-only internal comment on a provider. */
export interface ProviderCommentResponseDto {
  id: string;
  providerId: string;
  body: string;
  authorUserId: string | null;
  authorEmail: string | null;
  createdAt: string;
}

export interface CreateProviderRatingModifierData {
  category: ProviderRatingModifierCategory;
  scoreDelta: number;
  comment: string;
  startsAt?: string | null;
  expiresAt?: string | null;
}

export interface UpdateProviderRatingModifierData {
  category?: ProviderRatingModifierCategory;
  scoreDelta?: number;
  comment?: string;
  startsAt?: string | null;
  expiresAt?: string | null;
}

export interface ProviderRatingModifierResponseDto {
  id: string;
  providerId: string;
  category: ProviderRatingModifierCategory;
  scoreDelta: number;
  comment: string;
  startsAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedByUserId: string | null;
  authorUserId: string | null;
  authorEmail: string | null;
  createdAt: string;
  updatedAt: string;
}
